<template>
  <section class="stack-card">
    <h4 class="stack-card__title">Stack</h4>
    <div
      class="stack-card__rows"
      @mousedown="startStackDrag"
      @touchstart="startStackDrag"
      :class="{ 'is-dragging': isStackDragging }"
    >
      <!-- Top row — scrolls left -->
      <div class="stack-card__carousel">
        <div class="stack-card__track" ref="stackTrackTop" :style="stackTrackTopStyle">
          <div
            v-for="(item, index) in stackIconsTop"
            :key="'top-a-' + index"
            class="stack-icon"
            :data-name="item.name"
          >
            <component :is="item.icon" />
            <span class="stack-icon__name">{{ item.name }}</span>
          </div>
          <div
            v-for="(item, index) in stackIconsTop"
            :key="'top-b-' + index"
            class="stack-icon"
            :data-name="item.name"
          >
            <component :is="item.icon" />
            <span class="stack-icon__name">{{ item.name }}</span>
          </div>
        </div>
      </div>

      <!-- Bottom row — scrolls right (opposite) -->
      <div class="stack-card__carousel">
        <div class="stack-card__track" ref="stackTrackBottom" :style="stackTrackBottomStyle">
          <div
            v-for="(item, index) in stackIconsBottom"
            :key="'bot-a-' + index"
            class="stack-icon"
            :data-name="item.name"
          >
            <component :is="item.icon" />
            <span class="stack-icon__name">{{ item.name }}</span>
          </div>
          <div
            v-for="(item, index) in stackIconsBottom"
            :key="'bot-b-' + index"
            class="stack-icon"
            :data-name="item.name"
          >
            <component :is="item.icon" />
            <span class="stack-icon__name">{{ item.name }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, markRaw } from 'vue'
import {
  JavaScriptIcon,
  FigmaIcon,
  AdobeXdIcon,
  ArtificialIntelligence01Icon,
  WorkflowCircle01Icon,
  AiBrain01Icon,
} from 'hugeicons-vue'
import VueIcon from '@/components/icons/VueIcon.vue'
import GSAPIcon from '@/components/icons/GSAPIcon.vue'
import DockerIcon from '@/components/icons/DockerIcon.vue'
import TailwindCssIcon from '@/components/icons/TailwindCssIcon.vue'
import GitIcon from '@/components/icons/GitIcon.vue'
import Css3Icon from '@/components/icons/Css3Icon.vue'
import Html5Icon from '@/components/icons/Html5.vue'

// Top row — UX / Design / Philosophy
const stackIconsTop = [
  { name: 'Humanity Centered Design' },
  { name: 'Design for a Better World' },
  { name: 'Ethical Design' },
  { name: 'Inclusive Design' },
  { name: 'Systems Thinking' },
  { name: 'Product Design' },
  { name: 'UX Strategy' },
  { name: 'Design Systems' },
  { name: 'UX Research' },
  { name: 'Information Architecture' },
  { name: 'Figma', icon: markRaw(FigmaIcon) },
  { name: 'Adobe XD', icon: markRaw(AdobeXdIcon) },
  { name: 'Component Architecture' },
  { name: 'Accessibility (a11y)' },
]

// Bottom row — Engineering / AI / Tooling
const stackIconsBottom = [
  { name: 'HTML5', icon: markRaw(Html5Icon) },
  { name: 'CSS', icon: markRaw(Css3Icon) },
  { name: 'JavaScript', icon: markRaw(JavaScriptIcon) },
  { name: 'Vue Js', icon: markRaw(VueIcon) },
  { name: 'Tailwind CSS', icon: markRaw(TailwindCssIcon) },
  { name: 'GSAP', icon: markRaw(GSAPIcon) },
  { name: 'Performance Optimization' },
  { name: 'Agentic Workflows', icon: markRaw(WorkflowCircle01Icon) },
  { name: 'AI Integration', icon: markRaw(ArtificialIntelligence01Icon) },
  { name: 'Prompt Engineering', icon: markRaw(AiBrain01Icon) },
  { name: 'Git', icon: markRaw(GitIcon) },
  { name: 'Docker', icon: markRaw(DockerIcon) },
  { name: 'Digital Ocean' },
]

const stackCarousel = ref(null)
const stackTrackTop = ref(null)
const stackTrackBottom = ref(null)
const isStackDragging = ref(false)
const stackPositionTop = ref(0)
const stackPositionBottom = ref(0)
const autoScrollSpeed = 0.5

let stackDragStartX = 0
let stackDragStartPositionTop = 0
let stackDragStartPositionBottom = 0
let stackVelocity = 0
let stackLastX = 0
let stackLastTime = 0
let stackInertiaFrame = null
let stackAutoScrollFrame = null
let halfTrackWidthTop = 0
let halfTrackWidthBottom = 0

const stackTrackTopStyle = computed(() => ({
  transform: `translateX(${stackPositionTop.value}px)`,
}))

const stackTrackBottomStyle = computed(() => ({
  transform: `translateX(${stackPositionBottom.value}px)`,
}))

const wrapPosition = (pos, half) => {
  if (half === 0) return pos
  while (pos > 0) pos -= half
  while (pos < -half) pos += half
  return pos
}

const startStackDrag = (e) => {
  e.preventDefault()
  isStackDragging.value = true

  if (stackInertiaFrame) cancelAnimationFrame(stackInertiaFrame)
  if (stackAutoScrollFrame) cancelAnimationFrame(stackAutoScrollFrame)

  const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX
  stackDragStartX = clientX
  stackDragStartPositionTop = stackPositionTop.value
  stackDragStartPositionBottom = stackPositionBottom.value
  stackLastX = clientX
  stackLastTime = performance.now()
  stackVelocity = 0

  document.addEventListener('mousemove', onStackDrag)
  document.addEventListener('mouseup', stopStackDrag)
  document.addEventListener('touchmove', onStackDrag, { passive: false })
  document.addEventListener('touchend', stopStackDrag)
}

const onStackDrag = (e) => {
  if (!isStackDragging.value) return
  e.preventDefault()

  const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX
  const currentTime = performance.now()
  const deltaTime = currentTime - stackLastTime

  if (deltaTime > 0) {
    stackVelocity = ((clientX - stackLastX) / deltaTime) * 16
  }

  stackLastX = clientX
  stackLastTime = currentTime

  const deltaX = clientX - stackDragStartX
  stackPositionTop.value = wrapPosition(stackDragStartPositionTop + deltaX, halfTrackWidthTop)
  stackPositionBottom.value = wrapPosition(
    stackDragStartPositionBottom - deltaX,
    halfTrackWidthBottom
  )
}

const stopStackDrag = () => {
  if (!isStackDragging.value) return
  isStackDragging.value = false

  document.removeEventListener('mousemove', onStackDrag)
  document.removeEventListener('mouseup', stopStackDrag)
  document.removeEventListener('touchmove', onStackDrag)
  document.removeEventListener('touchend', stopStackDrag)

  if (Math.abs(stackVelocity) > 0.5) {
    applyInertia()
  } else {
    startAutoScroll()
  }
}

const applyInertia = () => {
  const friction = 0.95
  const minVelocity = 0.1

  const animate = () => {
    stackVelocity *= friction

    if (Math.abs(stackVelocity) < minVelocity) {
      stackVelocity = 0
      startAutoScroll()
      return
    }

    stackPositionTop.value = wrapPosition(stackPositionTop.value + stackVelocity, halfTrackWidthTop)
    stackPositionBottom.value = wrapPosition(
      stackPositionBottom.value - stackVelocity,
      halfTrackWidthBottom
    )

    stackInertiaFrame = requestAnimationFrame(animate)
  }

  stackInertiaFrame = requestAnimationFrame(animate)
}

const startAutoScroll = () => {
  if (stackAutoScrollFrame) cancelAnimationFrame(stackAutoScrollFrame)

  const autoScroll = () => {
    if (isStackDragging.value) return

    stackPositionTop.value = wrapPosition(
      stackPositionTop.value - autoScrollSpeed,
      halfTrackWidthTop
    )
    stackPositionBottom.value = wrapPosition(
      stackPositionBottom.value + autoScrollSpeed,
      halfTrackWidthBottom
    )

    stackAutoScrollFrame = requestAnimationFrame(autoScroll)
  }

  stackAutoScrollFrame = requestAnimationFrame(autoScroll)
}

const initStackCarousel = () => {
  if (stackTrackTop.value) halfTrackWidthTop = stackTrackTop.value.scrollWidth / 2
  if (stackTrackBottom.value) halfTrackWidthBottom = stackTrackBottom.value.scrollWidth / 2
  startAutoScroll()
}

onMounted(() => {
  initStackCarousel()
})

onUnmounted(() => {
  if (stackInertiaFrame) cancelAnimationFrame(stackInertiaFrame)
  if (stackAutoScrollFrame) cancelAnimationFrame(stackAutoScrollFrame)
  document.removeEventListener('mousemove', onStackDrag)
  document.removeEventListener('mouseup', stopStackDrag)
  document.removeEventListener('touchmove', onStackDrag)
  document.removeEventListener('touchend', stopStackDrag)
})
</script>

<style scoped>
.stack-card {
  @apply py-4 h-full relative w-full flex flex-col justify-center;
}

.stack-card__rows {
  display: flex;
  flex-direction: column;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
}

.stack-card__title {
  @apply text-xs uppercase tracking-wider mb-4 pl-6;
  color: var(--color-text-muted);
}

.stack-card__carousel {
  @apply relative overflow-hidden h-[50px] flex items-center w-full touch-none;
  mask-image: linear-gradient(to right, transparent 0%, black 10%, black 70%, transparent 100%);
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 10%,
    black 90%,
    transparent 100%
  );
}

.stack-card__rows.is-dragging {
  cursor: grabbing;
}

.stack-card__track {
  @apply flex gap-4;
  width: max-content;
  will-change: transform;
}

.stack-icon {
  @apply rounded-xl flex flex-row items-center justify-center transition-all duration-300 cursor-pointer flex-shrink-0 gap-2 px-3;
  height: 40px;
  background: var(--color-surface-elevated);
  pointer-events: none;
  /* Prevent icons from capturing drag events */
}

.stack-icon__name {
  @apply text-sm font-medium;
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.stack-icon svg {
  @apply w-6 h-6;
  color: var(--color-text-secondary);
}

.stack-icon:hover {
  background: var(--color-electric-purple);
  transform: translateY(-4px) scale(1.1);
}

.stack-icon:hover svg {
  color: white;
}
</style>
